import React, { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useQueryParameters from '@afosto/utils-react/useQueryParameters';
import useScanner from '@afosto/utils-react/input/useScanner';
import { useDataSource } from '@afosto/app-components-react/DataSource';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import ShipmentCard from '../../components/ShipmentCard';
import ShipmentCardSkeleton from '../../components/ShipmentCard/ShipmentCardSkeleton';
import DataList from '@afosto/app-components-react/DataList';
import Page from '@afosto/app-components-react/Page';
import SearchBar from '@afosto/app-components-react/SearchBar';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import searchQuery from '@afosto/utils-react/datagrid/searchQuery';
import { handling as handlingApi } from '@afosto/fulfilment/api/Handling';
import translations from './translations';

const dataListContainerGrid = props => <Grid container spacing={1} {...props} />;

const SHIPMENTS_PROFILE_CODE = 'lcs';
const SHIPMENTS_PROFILE_MODEL = 'shipments';
const SHIPMENTS_PROFILE_NAMESPACE = 'default';

const Packing = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:shipments:read',
    'lcs:locations:read',
    'rel:contacts:read',
    'lcs:handling:read',
  ]);

  const intl = useIntl();
  const history = useHistory();
  const { list_id: listID } = useQueryParameters();
  const [inputFocus, setInputFocus] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const searchIdentifier = {
    code: SHIPMENTS_PROFILE_CODE,
    model: SHIPMENTS_PROFILE_MODEL,
    namespace: SHIPMENTS_PROFILE_NAMESPACE,
  };

  const fetchShipments = async data => {
    try {
      const { activeFilters } = data.filters;
      const otherFilters = activeFilters.filter(filter => filter.key !== 'number');
      const newFilters = [
        ...otherFilters,
        ...(!listID && searchValue?.length > 0
          ? [
              {
                key: 'number',
                operator: 'in',
                value: [searchValue?.toLowerCase()],
              },
            ]
          : []),
        ...(!listID
          ? [
              {
                key: 'is_handled',
                operator: 'in',
                value: [true],
              },
            ]
          : []),
        ...(listID
          ? [
              {
                key: 'list_id',
                operator: 'in',
                value: [listID],
              },
            ]
          : []),
        {
          key: 'is_outbound',
          operator: 'in',
          value: [true],
        },
        {
          key: 'is_boxed',
          operator: 'in',
          value: [false],
        },
        {
          key: 'is_finished',
          operator: 'in',
          value: [false],
        },
        {
          key: 'is_canceled',
          operator: 'in',
          value: [false],
        },
      ];

      const { activeSort } = data.sorting;
      let handlingList = null;

      if (listID) {
        const handlingListResponse = await handlingApi.viewHandlingList(listID);
        handlingList = handlingListResponse?.data ?? null;
      }

      const searchData = await searchQuery(searchIdentifier)({
        ...data,
        filters: { ...data.filters, activeFilters: newFilters },
        sorting: {
          ...data.sorting,
          activeSort:
            activeSort.length > 0 ? activeSort : [{ key: 'expected_at', direction: 'asc' }],
        },
      });

      const formattedShipments = searchData.reduce((acc, shipment) => {
        const sortLabels = handlingList?.sorting_labels ?? [];
        const label = findObjectInArray(sortLabels, 'shipment_id', shipment?.id);

        return [...acc, { ...shipment, crate: label?.label }];
      }, []);
      const filteredShipments = formattedShipments.filter(shipment => !shipment.is_boxed);

      if (formattedShipments.length === 1 && !filteredShipments?.length) {
        setSearchValue('');
        enqueueSnackbar(intl.formatMessage(translations.feedback.shipmentAlreadyBoxed), {
          variant: 'info',
        });
      }

      return filteredShipments;
    } catch (err) {
      return [];
    }
  };

  const dataSource = useDataSource(fetchShipments, {
    modules: ['dataProperties', 'filters', 'sorting', 'pagination'],
  });
  const { data: shipments = [], isLoadingData } = dataSource;

  const handleSearch = value => {
    setSearchValue(value);
    dataSource.reloadDataSource();
  };

  useScanner(handleSearch, { disabled: !!listID || inputFocus });

  /**
   * Render the shipment in the data list.
   * @param {object} shipment
   * @returns {*}
   */
  const renderShipment = shipment => (
    <Grid key={shipment.id} item xs={12}>
      <ShipmentCard
        shipment={shipment}
        onClick={() => history.push(`/packing/${shipment.id}${listID ? `?list_id=${listID}` : ''}`)}
        ignoreMissingItems
      />
      <Divider />
    </Grid>
  );

  const renderShipmentSkeleton = key => (
    <Grid key={key} item xs={12}>
      <ShipmentCardSkeleton />
    </Grid>
  );

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)}>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Page title={intl.formatMessage(translations.title)} fullWidth>
      {!listID && (
        <SearchBar
          position="static"
          value={searchValue}
          searchInputProps={{
            placeholder: intl.formatMessage(translations.search.placeholder),
            onBlur: () => setInputFocus(false),
            onFocus: () => setInputFocus(true),
          }}
          onSearch={handleSearch}
        />
      )}
      <DataList
        renderItem={renderShipment}
        renderSkeletonItem={renderShipmentSkeleton}
        dataSource={dataSource}
        component={dataListContainerGrid}
        emptyMessage={intl.formatMessage(
          translations.emptyMessage?.[listID ? 'list' : 'shipments'],
        )}
      />
      {listID && !isLoadingData && shipments.length === 0 && (
        <Box mt={2}>
          <Button variant="contained" color="primary" onClick={() => history.push('/shipments')}>
            <FormattedMessage {...translations.backToShipments} />
          </Button>
        </Box>
      )}
    </Page>
  );
};

export default Packing;
